import { useId, type HTMLAttributes } from "react";
import { cx } from "@/lib/cx";

/** Top-right corner notch. The mono index sits in it. */
const CORNER = "M0 0H0.655C0.686 0 0.7 0.014 0.7 0.048V0.125C0.7 0.16 0.714 0.174 0.745 0.174H1V1H0V0Z";

export type ProjectCardProps = Omit<HTMLAttributes<HTMLElement>, "title"> & {
  title: string;
  src: string;
  alt?: string;
  meta?: string;
  tags?: string[];
  index?: string;
  href?: string;
  ratio?: string;
  cut?: "corner" | "none";
};

export function ProjectCard({
  title,
  src,
  alt = "",
  meta,
  tags = [],
  index,
  href,
  ratio = "4/3",
  cut = "corner",
  className,
  ...rest
}: ProjectCardProps) {
  const clipId = `datum-corner-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const clipped = cut === "corner";

  const body = (
    <>
      {clipped && (
        <svg width="0" height="0" className="absolute" aria-hidden="true">
          <defs>
            <clipPath id={clipId} clipPathUnits="objectBoundingBox">
              <path d={CORNER} />
            </clipPath>
          </defs>
        </svg>
      )}

      <div
        className="relative overflow-hidden bg-surface-inset"
        style={{
          aspectRatio: ratio,
          clipPath: clipped ? `url(#${clipId})` : undefined,
          WebkitClipPath: clipped ? `url(#${clipId})` : undefined,
        }}
      >
        <img
          src={src}
          alt={alt}
          className={cx(
            "w-full h-full object-cover plate-img",
            "transition-transform duration-500 ease-out",
            href && "group-hover:scale-[1.03]",
          )}
        />
      </div>

      {index && clipped && (
        <span className="absolute top-0 right-0 font-label text-label text-text-faint">{index}</span>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex justify-between gap-4 items-baseline">
          <h3
            className={cx(
              "font-display font-regular text-[22px] leading-[1.14] text-text-primary",
              "transition-colors duration-160 ease-out",
              href && "group-hover:text-signal-500",
            )}
          >
            {title}
          </h3>
          {index && !clipped && <span className="font-label text-label text-text-faint">{index}</span>}
        </div>

        {meta && <p className="text-body-sm text-text-body max-w-[46ch]">{meta}</p>}

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1">
            {tags.map((t) => (
              <span
                key={t}
                className="font-label text-label uppercase text-text-muted border border-border-hairline rounded-pill px-[10px] py-1"
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const shared = "group relative flex flex-col gap-4 text-inherit";

  // Only a card that actually links anywhere becomes a link. The compiled
  // bundle fell back to href="#", which makes a click jump to the top of the page.
  if (href) {
    return (
      <a href={href} className={cx(shared, "no-underline border-none")} {...rest}>
        {body}
      </a>
    );
  }

  return (
    <article className={cx(shared, className)} {...rest}>
      {body}
    </article>
  );
}
